import { PureComponent, ReactNode } from 'react';
import getCareCanvasTemplates from '../../../../../api/getCareCanvasTemplates';
import { notification } from 'antd';
import { captureError } from '../../../../../pages/captureError';
import { track } from '../../../../../tracking';
import { CareCanvasTemplate } from '../../../../../store/types/CareCanvas';
import Fuse from 'fuse.js';
import { templateIcon } from '../../../../common/Icons';
import deleteCareCanvasTemplate from '../../../../../api/deleteCareCanvasTemplate';
import { WarningModal } from './WarningModal';

const fuseConfig = { keys: ['name'], threshold: 0.2 };

type Props = {
    onCancel: () => void;
    applyTemplate: (template: CareCanvasTemplate) => void;
    docid: string;
    setPreviewTemplate: (template?: CareCanvasTemplate) => void;
};

type State = {
    search?: string;
    templates: CareCanvasTemplate[];
    isFetchingTemplates?: boolean;
    isDeletingTemplate?: boolean;
};

class UseTemplate extends PureComponent<Props, State> {
    state: State = {
        templates: [],
    };

    fuse = new Fuse([] as CareCanvasTemplate[], fuseConfig);

    componentDidMount = () => {
        const { fetchTemplates } = this;
        fetchTemplates();
    };

    fetchTemplates = async () => {
        const { docid } = this.props;
        this.setState({
            isFetchingTemplates: true,
        });
        try {
            const { templates } = await getCareCanvasTemplates(docid);
            this.setState({ templates: templates || [] });
            this.fuse = new Fuse(templates || [], fuseConfig);
        } catch (error) {
            notification.error({
                message: 'Failed to get canvas templates',
                duration: 5,
            });
            captureError(error as Error);
            track('ERROR', { on: 'getCareCanvasTemplates', what: (error as Error).message });
        } finally {
            this.setState({
                isFetchingTemplates: false,
            });
        }
    };

    onSearchChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        this.setState({
            search: e.target.value,
        });
    };

    deleteTemplate = async (id: string) => {
        this.setState({
            isDeletingTemplate: true,
        });
        try {
            const { success, error } = await deleteCareCanvasTemplate(id);
            if (success) {
                notification.success({
                    message: 'Canvas template deleted successfully',
                    duration: 5,
                });
                this.fetchTemplates();
                track('CARE_CANVAS_TEMPLATE_DELETE_SUCCESS');
            } else {
                notification.error({
                    message: 'Failed to delete canvas template',
                    duration: 5,
                });
                track('CARE_CANVAS_TEMPLATE_DELETE_FAILED', { error });
            }
        } catch (error) {
            notification.error({
                message: 'Failed to delete canvas template',
                duration: 5,
            });
            captureError(error as Error);
            track('ERROR', { on: 'deleteCareCanvasTemplate', what: (error as Error).message });
        } finally {
            this.setState({
                isDeletingTemplate: false,
            });
        }
    };

    onDeleteTemplate = (id: string) => {
        const { deleteTemplate } = this;
        const mainText = 'Are you sure you want to delete this template?';
        const subText = 'This template will be permanently lost and cannot be recovered later.';
        const onContinue = () => deleteTemplate(id);
        WarningModal(mainText, subText, onContinue);
    };

    render(): ReactNode {
        const { onCancel, applyTemplate, setPreviewTemplate } = this.props;
        const { search, templates, isFetchingTemplates, isDeletingTemplate } = this.state;
        const { onSearchChange, onDeleteTemplate } = this;

        const filteredTemplates = search
            ? this.fuse.search(search).map((option) => option.item)
            : templates;

        return (
            <div className="space-y-16 rounded-8 py-16 global-template-popup popup flex flex-col border border-neutral-7 w-420 h-296">
                <div className="px-16 flex items-center justify-between ">
                    <p className="text-darwin-neutral-1000 font-700 text-13">
                        Search Canvas Template
                    </p>
                    <div
                        onClick={(e) => {
                            onCancel();
                            e.stopPropagation();
                        }}
                        className="flex items-center space-x-4 cursor-pointer"
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 320 512"
                            className="w-16 h-16 fill-neutral-3 hover:fill-neutral-1"
                        >
                            <path d="M193.94 256L296.5 153.44l21.15-21.15c3.12-3.12 3.12-8.19 0-11.31l-22.63-22.63c-3.12-3.12-8.19-3.12-11.31 0L160 222.06 36.29 98.34c-3.12-3.12-8.19-3.12-11.31 0L2.34 120.97c-3.12 3.12-3.12 8.19 0 11.31L126.06 256 2.34 379.71c-3.12 3.12-3.12 8.19 0 11.31l22.63 22.63c3.12 3.12 8.19 3.12 11.31 0L160 289.94 262.56 392.5l21.15 21.15c3.12 3.12 8.19 3.12 11.31 0l22.63-22.63c3.12-3.12 3.12-8.19 0-11.31L193.94 256z"></path>
                        </svg>
                    </div>
                </div>

                <div className="px-16">
                    <div className="px-12 w-full rounded-8 border border-neutral-7 flex items-center space-x-10 focus-within:bg-neutral-9 bg-white">
                        <div className="flex items-center space-x-12">
                            <svg
                                className="w-16 h-16 fill-neutral-black"
                                role="img"
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 512 512"
                            >
                                <path d="M504.1 471l-134-134C399.1 301.5 415.1 256.8 415.1 208c0-114.9-93.13-208-208-208S-.0002 93.13-.0002 208S93.12 416 207.1 416c48.79 0 93.55-16.91 129-45.04l134 134C475.7 509.7 481.9 512 488 512s12.28-2.344 16.97-7.031C514.3 495.6 514.3 480.4 504.1 471zM48 208c0-88.22 71.78-160 160-160s160 71.78 160 160s-71.78 160-160 160S48 296.2 48 208z"></path>
                            </svg>
                        </div>
                        <input
                            type="text"
                            autoFocus
                            className="w-full text-14 placeholder-darwin-neutral-500 text-neutral-black bg-transparent py-10 disabled:opacity-70 disabled:cursor-not-allowed"
                            placeholder="Start typing canvas name"
                            value={search}
                            onChange={onSearchChange}
                            disabled={isFetchingTemplates}
                        />
                    </div>
                </div>

                <div className="flex-1 flex-auto overflow-auto w-full pt-4">
                    {isFetchingTemplates ? (
                        <div className="h-full flex items-center justify-center">
                            <svg
                                className="w-16 h-16 fill-darwin-accent-bright-blue-800 animate-spin"
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 512 512"
                            >
                                <path d="M96 256c0-26.5-21.5-48-48-48S0 229.5 0 256s21.5 48 48 48S96 282.5 96 256zM108.9 60.89c-26.5 0-48.01 21.49-48.01 47.99S82.39 156.9 108.9 156.9s47.99-21.51 47.99-48.01S135.4 60.89 108.9 60.89zM108.9 355.1c-26.5 0-48.01 21.51-48.01 48.01S82.39 451.1 108.9 451.1s47.99-21.49 47.99-47.99S135.4 355.1 108.9 355.1zM256 416c-26.5 0-48 21.5-48 48S229.5 512 256 512s48-21.5 48-48S282.5 416 256 416zM464 208C437.5 208 416 229.5 416 256s21.5 48 48 48S512 282.5 512 256S490.5 208 464 208zM403.1 355.1c-26.5 0-47.99 21.51-47.99 48.01S376.6 451.1 403.1 451.1s48.01-21.49 48.01-47.99S429.6 355.1 403.1 355.1zM256 0C229.5 0 208 21.5 208 48S229.5 96 256 96s48-21.5 48-48S282.5 0 256 0z"></path>
                            </svg>
                        </div>
                    ) : filteredTemplates.length > 0 ? (
                        filteredTemplates.map((t) => (
                            <div
                                key={t._id}
                                className="px-20 pb-8 flex items-center space-x-16 cursor-pointer hover-parent-element"
                                onClick={(e) => {
                                    applyTemplate(t);
                                    onCancel();
                                    e.stopPropagation();
                                }}
                            >
                                <div className="relative pl-0 p-12">
                                    {templateIcon('w-16 h-16 fill-darwin-accent-bright-blue-800')}
                                    <p className="absolute select-none bottom-4 right-0 text-12 font-700 text-darwin-accent-bright-blue-800">
                                        Cx
                                    </p>
                                </div>
                                <div className="relative flex items-center w-full justify-between py-2 hover-parent-element">
                                    <div>
                                        <p className="text-neutral-black font-700 text-14">
                                            {t.name}
                                        </p>
                                    </div>
                                    <div className="absolute right-0 space-x-8 flex items-center hover-child-element">
                                        <button
                                            className="text-darwin-accent-bright-blue-800 font-700 text-12 border rounded-4 px-8 py-3 relative -top-2"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setPreviewTemplate(t);
                                                onCancel();
                                            }}
                                        >
                                            Preview
                                        </button>
                                        <button
                                            className="text-red-primary font-700 text-12 border rounded-4 px-6 py-4 relative top-1 disabled:opacity-70 disabled:cursor-not-allowed"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onDeleteTemplate(t._id);
                                            }}
                                            disabled={isDeletingTemplate}
                                        >
                                            <svg
                                                viewBox="64 64 896 896"
                                                className="fill-red-primary w-16 h-16"
                                            >
                                                <path d="M360 184h-8c4.4 0 8-3.6 8-8v8h304v-8c0 4.4 3.6 8 8 8h-8v72h72v-80c0-35.3-28.7-64-64-64H352c-35.3 0-64 28.7-64 64v80h72v-72zm504 72H160c-17.7 0-32 14.3-32 32v32c0 4.4 3.6 8 8 8h60.4l24.7 523c1.6 34.1 29.8 61 63.9 61h454c34.2 0 62.3-26.8 63.9-61l24.7-523H888c4.4 0 8-3.6 8-8v-32c0-17.7-14.3-32-32-32zM731.3 840H292.7l-24.2-512h487l-24.2 512z"></path>
                                            </svg>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))
                    ) : (
                        <div className="mb-16 text-left">
                            <span className="px-20">No canvas found</span>
                        </div>
                    )}
                </div>
            </div>
        );
    }
}

export default UseTemplate;
